import { InfoCircleOutlined } from "@ant-design/icons";
import { integrationRegistry } from "@p0security/shared/integrations/registry";
import {
  Filter,
  IntegrationResourceRule,
} from "@p0security/shared/types/policy/types";
import { App, Popover, Space } from "@p0security/ui";
import { widetype } from "@p0security/util/collections";
import { isKeyOf } from "@p0security/util/types/is";
import { AllIntegrationLogos } from "components/Integrations/IntegrationLogo";
import {
  ANY_ACCESS_TYPE_ICON,
  accessTypeIcon,
} from "components/Jit/Policy/accessTypeIcons";
import { EditorBackButton } from "components/Jit/Policy/components/EditorBackButton";
// The shared ResourceFilterEditor reads the filter-editing context exported
// by the legacy ResourceEditor module, so this duplicate must provide that
// same context object rather than defining its own.
import { LocalResourceFilterContext } from "components/Jit/Policy/components/editors/ResourceEditor";
import { ResourceFilterEditor } from "components/Jit/Policy/components/editors/ResourceFilterEditor";
import { ResourceFilterPreview } from "components/Jit/Policy/components/previews/ResourceFilterPreview";
import {
  accessTypeOptionsByResource,
  filterTypeOptionsByResource,
} from "components/Jit/Policy/constants";
import { useInstalledIntegrations } from "components/Jit/Policy/providers/InstalledIntegrationsProvider";
import { PolicyEditorContext } from "components/Jit/Policy/store/RoutingRuleEditorContext";
import { getResourceFilterEntries } from "components/Jit/Policy/utils";
import { EditableList } from "components/common/EditableList";
import { FormRow, RowLabel } from "components/common/FormRow";
import { PillSelect, PillSelectOption } from "components/common/PillSelect";
import { capitalize } from "lodash";
import { useCallback, useContext, useMemo, useState } from "react";
import { keyLabelObjectToOptions } from "utils/antd-utilities";

type Service = IntegrationResourceRule["service"];

// Mirrors the legacy policy editor's integration drill-down. "bundle" is
// excluded here because Access Bundle is its own top-level choice in the
// studio's resource editor. Only integrations installed for the tenant are
// offered, since policies scoped to an uninstalled integration can never
// match any resource.
const buildIntegrationOptions = (
  installedKeys: Set<string>
): PillSelectOption<Service>[] =>
  widetype
    .entries(integrationRegistry)
    .filter(([value]) => {
      const service = value as Service;
      return (
        service in accessTypeOptionsByResource &&
        service !== "bundle" &&
        installedKeys.has(service)
      );
    })
    .map(([value, { label }]) => ({
      icon: AllIntegrationLogos[value],
      label,
      searchText: label,
      value: value as Service,
    }));

const integrationAccessTypeOptions = (
  integration: string
): PillSelectOption<string>[] => {
  if (!(integration in accessTypeOptionsByResource)) return [];
  const perService =
    accessTypeOptionsByResource[
      integration as keyof typeof accessTypeOptionsByResource
    ];
  return [
    { icon: ANY_ACCESS_TYPE_ICON, label: "Any access type", value: "any" },
    ...(perService
      ? perService.map((key) => ({
          icon: accessTypeIcon(key),
          label: capitalize(key),
          value: key,
        }))
      : []),
  ];
};

type Props = {
  onResourceFilterEditInProgress: (editing: boolean) => void;
};

/** Drill-down for a specific integration's resources: pick the integration,
 *  then the access type and optional resource filters. */
export const SpecificResourceEditor: React.FC<Props> = ({
  onResourceFilterEditInProgress,
}) => {
  const { resource, setResource, setResourceAccessType, removeResourceFilter } =
    useContext(PolicyEditorContext);
  const { modal } = App.useApp();
  const { installedResources } = useInstalledIntegrations();

  const integrationOptions = useMemo(() => {
    const installedKeys = new Set(installedResources.map((i) => i.key));
    return buildIntegrationOptions(installedKeys);
  }, [installedResources]);

  // Undefined until an integration is chosen (e.g. right after switching the
  // top-level choice to "Specific resource").
  const currentService: Service | undefined =
    resource?.type === "integration" && resource.service !== "bundle"
      ? resource.service
      : undefined;

  const [isAddingNewFilter, setIsAddingNewFilter] = useState(false);
  const [editingFilterIndex, setEditingFilterIndex] = useState<number | null>(
    null
  );

  const [localFilterType, setLocalFilterType] = useState<string>();
  const [localFilterEffect, setLocalFilterEffect] =
    useState<Filter["effect"]>();
  const [localFilterPattern, setLocalFilterPattern] = useState<string>();
  const [localFilterKey, setLocalFilterKey] = useState<string>();
  const [localFilterValue, setLocalFilterValue] = useState<boolean>();

  const resetFilterEditingState = useCallback(() => {
    setIsAddingNewFilter(false);
    setEditingFilterIndex(null);
    setLocalFilterType(undefined);
    setLocalFilterEffect(undefined);
    setLocalFilterPattern(undefined);
    setLocalFilterKey(undefined);
    setLocalFilterValue(undefined);
    onResourceFilterEditInProgress(false);
  }, [onResourceFilterEditInProgress]);

  const handleSelectService = useCallback(
    (next: Service) => {
      if (next === currentService) return;
      const apply = () => {
        resetFilterEditingState();
        setResource({
          type: "integration",
          service: next,
          accessType: "any",
          filters: {},
        });
      };

      const hasFilters = getResourceFilterEntries(resource).length > 0;
      if (hasFilters) {
        modal.confirm({
          title: "Discard resource filters?",
          content: `Switching to "${isKeyOf(integrationRegistry, next) ? integrationRegistry[next].label : next}" removes the filters you've configured.`,
          okText: "Discard filters",
          okType: "danger",
          cancelText: "Keep editing",
          onOk: apply,
        });
        return;
      }
      apply();
    },
    [currentService, modal, resetFilterEditingState, resource, setResource]
  );

  const filterEntries = useMemo(
    () => getResourceFilterEntries(resource),
    [resource]
  );

  const filterListItems = useMemo(
    () => filterEntries.map(([type, value]) => ({ type, value })),
    [filterEntries]
  );

  const availableFilterTypes = useMemo(() => {
    if (resource?.type !== "integration") {
      return [];
    }
    const used = resource.filters ? Object.keys(resource.filters) : [];
    const options =
      filterTypeOptionsByResource[
        resource.service as keyof typeof filterTypeOptionsByResource
      ];
    if (!options) return [];
    return keyLabelObjectToOptions(options).filter(
      ({ value }) => !used.includes(value)
    );
  }, [resource]);

  const resourceFilterDirty = useMemo(() => {
    if (resource?.type !== "integration") {
      return false;
    }
    if (isAddingNewFilter) {
      return (
        !!localFilterType ||
        !!localFilterEffect ||
        !!localFilterPattern ||
        !!localFilterKey ||
        localFilterValue !== undefined
      );
    }
    if (editingFilterIndex !== null && resource.filters) {
      const entry = filterEntries[editingFilterIndex];
      if (!entry) return false;
      const [fType, f] = entry;
      if (fType !== localFilterType || f?.effect !== localFilterEffect)
        return true;

      return (
        fType !== localFilterType ||
        f?.effect !== localFilterEffect ||
        (f.effect !== "removeAll" &&
          "key" in f &&
          (f.pattern !== localFilterPattern || f.key !== localFilterKey)) ||
        ("value" in f && f.value !== localFilterValue)
      );
    }
    return false;
  }, [
    resource,
    isAddingNewFilter,
    editingFilterIndex,
    localFilterType,
    localFilterEffect,
    localFilterPattern,
    localFilterKey,
    localFilterValue,
    filterEntries,
  ]);

  const handleStartAddingNewFilter = useCallback(() => {
    setLocalFilterType(undefined);
    setLocalFilterEffect(undefined);
    setLocalFilterPattern(undefined);
    setLocalFilterKey(undefined);
    setIsAddingNewFilter(true);
    onResourceFilterEditInProgress(true);
  }, [setIsAddingNewFilter, onResourceFilterEditInProgress]);

  const handleStartEditingFilter = useCallback(
    (index: number) => {
      const [fType, f] = filterEntries[index];
      setLocalFilterType(fType);
      setLocalFilterEffect(f.effect);
      if (f.effect !== "removeAll") {
        if ("value" in f) {
          setLocalFilterValue(f.value);
        } else {
          setLocalFilterPattern(f.pattern);
          setLocalFilterKey(f.key);
        }
      } else {
        setLocalFilterPattern(undefined);
        setLocalFilterKey(undefined);
        setLocalFilterValue(undefined);
      }
      setEditingFilterIndex(index);
      onResourceFilterEditInProgress(true);
    },
    [filterEntries, setEditingFilterIndex, onResourceFilterEditInProgress]
  );

  const handleBackToList = useCallback(() => {
    setIsAddingNewFilter(false);
    setEditingFilterIndex(null);
    onResourceFilterEditInProgress(false);
  }, [
    setEditingFilterIndex,
    setIsAddingNewFilter,
    onResourceFilterEditInProgress,
  ]);

  const localFilterContextValue = useMemo(
    () => ({
      localFilterType,
      setLocalFilterType,
      localFilterEffect,
      setLocalFilterEffect,
      localFilterKey,
      setLocalFilterKey,
      localFilterPattern,
      setLocalFilterPattern,
      localFilterValue,
      setLocalFilterValue,
      isDirty: resourceFilterDirty,
      isNew: isAddingNewFilter,
      onSave: handleBackToList,
    }),
    [
      localFilterType,
      localFilterEffect,
      localFilterKey,
      localFilterPattern,
      localFilterValue,
      resourceFilterDirty,
      isAddingNewFilter,
      handleBackToList,
    ]
  );

  const handleRemoveFilter = useCallback(
    (index: number) => {
      const [fType, f] = filterEntries[index];
      if (fType && f) {
        removeResourceFilter(fType);
      }
    },
    [removeResourceFilter, filterEntries]
  );

  const renderResourceFilterPreview = useCallback(
    (filter: { value: Filter; type: string }) => (
      <ResourceFilterPreview filter={filter.value} filterType={filter.type} />
    ),
    []
  );

  const accessTypeOptions = useMemo(() => {
    if (resource?.type !== "integration") {
      return [];
    }
    return integrationAccessTypeOptions(resource.service);
  }, [resource]);

  return (
    <Space direction="vertical" size="small" className="w-full">
      <FormRow>
        <PillSelect<Service>
          options={integrationOptions}
          value={currentService}
          onChange={handleSelectService}
          placeholder="Select an integration"
          searchable
        />
      </FormRow>
      {currentService !== undefined && resource?.type === "integration" && (
        <>
          <FormRow>
            <RowLabel width={20}>Access type</RowLabel>
            <PillSelect
              value={resource.accessType ?? "any"}
              options={accessTypeOptions}
              onChange={setResourceAccessType}
            />
            {(!resource.accessType || resource.accessType === "any") && (
              <Popover
                content={
                  <div style={{ maxWidth: 300 }}>
                    Pick a more specific access type, especially when using
                    filters.
                  </div>
                }
                trigger="hover"
              >
                <InfoCircleOutlined className="text-[#595959]" />
              </Popover>
            )}
          </FormRow>
          <EditableList
            items={filterListItems}
            onEditItem={handleStartEditingFilter}
            onAddItem={handleStartAddingNewFilter}
            onDeleteItem={handleRemoveFilter}
            renderItem={renderResourceFilterPreview}
            addButtonText="Add New Resource Filter"
            addButtonDisabled={availableFilterTypes.length === 0}
            editing={isAddingNewFilter || editingFilterIndex !== null}
          />
          {(isAddingNewFilter || editingFilterIndex !== null) && (
            <LocalResourceFilterContext.Provider
              value={localFilterContextValue}
            >
              <ResourceFilterEditor
                cancelButton={
                  <EditorBackButton
                    onBack={handleBackToList}
                    isDirty={resourceFilterDirty}
                    isAdding={isAddingNewFilter}
                    name="Resource Filter"
                  />
                }
                resource={resource}
                filterType={
                  editingFilterIndex !== null
                    ? filterEntries[editingFilterIndex][0]
                    : undefined
                }
              />
            </LocalResourceFilterContext.Provider>
          )}
        </>
      )}
    </Space>
  );
};
